import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { CheckCircle2, Database, FileCheck, Zap } from 'lucide-react';
const COLUMN_WIDTH = 400;
const START_X = 80;
const START_Y = 80;
const MIN_ROW_GAP = 200;
const ROW_TOLERANCE = 50;
const BASE_GAP = 180;
const LANE_THICKNESS = 60;
const SVG_WIDTH = 1760;
// Cores por direção da conexão (para baixo, para cima, para trás, para frente) com os tokens de gráfico do tema.
const CONNECTION_COLORS = { cyan: 'hsl(var(--chart-1))', purple: 'hsl(var(--chart-2))', orange: 'hsl(var(--chart-3))', green: 'hsl(var(--chart-4))' };
const FIELD_TONES = [
    { border: 'hsl(var(--chart-3))', chip: { backgroundColor: 'hsl(var(--chart-3) / 0.15)', color: 'hsl(var(--chart-3))' } },
    { border: 'hsl(var(--chart-2))', chip: { backgroundColor: 'hsl(var(--chart-2) / 0.15)', color: 'hsl(var(--chart-2))' } },
    { border: 'hsl(var(--chart-1))', chip: { backgroundColor: 'hsl(var(--chart-1) / 0.15)', color: 'hsl(var(--chart-1))' } },
    { border: 'hsl(var(--chart-4))', chip: { backgroundColor: 'hsl(var(--chart-4) / 0.15)', color: 'hsl(var(--chart-4))' } },
];
function calculateCardHeight(fieldsCount) {
    return 220 + Math.min(fieldsCount || 0, 3) * 50;
}
function parseFieldType(fieldId) {
    const type = fieldId.split('_')[0];
    const typeMap = { button: 'Botão', option: 'Opções', options: 'Opções', form: 'Formulário', slider: 'Régua/Slider' };
    const displayType = typeMap[type] || type.charAt(0).toUpperCase() + type.slice(1);
    const shortId = fieldId.length > 25 ? fieldId.substring(0, 25) + '...' : fieldId;
    return { type: displayType, displayName: shortId };
}
function FieldIcon({ type, className }) {
    if (type === 'Opções' || type === 'Régua/Slider')
        return _jsx(Zap, { className: className });
    if (type === 'Formulário')
        return _jsx(FileCheck, { className: className });
    return _jsx(CheckCircle2, { className: className });
}
function convert(board, pageView) {
    const nodes = [];
    const conns = [];
    const rowHeights = [];
    const totalRows = Math.ceil(board.length / 4);
    for (let i = 0; i < totalRows; i++) {
        const rowItems = board.slice(i * 4, (i + 1) * 4);
        rowHeights.push(rowItems.length ? Math.max(...rowItems.map((item) => { var _a; return calculateCardHeight(((_a = item.fields) === null || _a === void 0 ? void 0 : _a.length) || 0); })) + MIN_ROW_GAP : MIN_ROW_GAP);
    }
    board.forEach((item, index) => {
        const row = Math.floor(index / 4);
        const col = index % 4;
        const fields = item.fields || [];
        const outputsTotal = (item.connections || []).filter((c) => (c === null || c === void 0 ? void 0 : c.id) !== item.identifier).reduce((sum, c) => sum + (Number(c === null || c === void 0 ? void 0 : c.total) || 0), 0);
        nodes.push({
            id: item.identifier,
            x: START_X + col * COLUMN_WIDTH,
            y: START_Y + rowHeights.slice(0, row).reduce((sum, h) => sum + h, 0),
            row,
            col,
            title: item.name,
            stepNumber: item.position + 1,
            avgTimeSeconds: item.avg_time_seconds,
            fieldsCount: fields.length,
            fields: fields.map((id) => ({ id, ...parseFieldType(id) })),
            outputsCount: outputsTotal,
            inputsCount: 0,
        });
    });
    const inputs = new Map();
    board.forEach((item) => (item.connections || []).forEach((c) => {
        if (c.id === item.identifier)
            return;
        inputs.set(c.id, (inputs.get(c.id) || 0) + (Number(c === null || c === void 0 ? void 0 : c.total) || 0));
    }));
    nodes.forEach((node) => {
        node.inputsCount = inputs.get(node.id) || 0;
        if (node.stepNumber == 1)
            node.inputsCount = pageView;
    });
    board.forEach((item, fromIndex) => {
        (item.connections || []).forEach((c, connIdx) => {
            const toIndex = board.findIndex((b) => b.identifier === c.id);
            if (toIndex === -1 || toIndex === fromIndex)
                return;
            const fromRow = Math.floor(fromIndex / 4);
            const fromCol = fromIndex % 4;
            const toRow = Math.floor(toIndex / 4);
            const toCol = toIndex % 4;
            let color = 'cyan';
            if (toRow > fromRow)
                color = 'cyan';
            else if (toRow < fromRow)
                color = 'purple';
            else
                color = toCol > fromCol ? 'green' : 'orange';
            conns.push({ id: `${item.identifier}-to-${c.id}-${connIdx}`, from: item.identifier, to: c.id, color });
        });
    });
    return { nodes, connections: conns };
}
function buildSmoothPath(points, smoothness = 30) {
    if (points.length < 2)
        return '';
    if (points.length === 2)
        return `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y}`;
    let path = `M ${points[0].x} ${points[0].y}`;
    for (let i = 1; i < points.length - 1; i++) {
        const prev = points[i - 1];
        const curr = points[i];
        const next = points[i + 1];
        const dx1 = curr.x - prev.x;
        const dy1 = curr.y - prev.y;
        const dx2 = next.x - curr.x;
        const dy2 = next.y - curr.y;
        const dist1 = Math.sqrt(dx1 * dx1 + dy1 * dy1);
        const dist2 = Math.sqrt(dx2 * dx2 + dy2 * dy2);
        if (dist1 === 0 || dist2 === 0) {
            path += ` L ${curr.x} ${curr.y}`;
            continue;
        }
        const radius = Math.min(smoothness, dist1 * 0.5, dist2 * 0.5);
        const t1 = radius / dist1;
        const t2 = radius / dist2;
        path += ` L ${curr.x - dx1 * t1} ${curr.y - dy1 * t1}`;
        path += ` Q ${curr.x} ${curr.y} ${curr.x + dx2 * t2} ${curr.y + dy2 * t2}`;
    }
    const last = points[points.length - 1];
    return path + ` L ${last.x} ${last.y}`;
}
/** Reposiciona as linhas conforme a altura real dos cards e o número de conexões entre elas. */
function layout(nodes, connections, dims) {
    const sorted = [...nodes].sort((a, b) => a.y - b.y);
    const bands = [];
    const nodeToRow = new Map();
    sorted.forEach((node) => {
        var _a, _b;
        const height = (_b = (_a = dims.get(node.id)) === null || _a === void 0 ? void 0 : _a.height) !== null && _b !== void 0 ? _b : 400;
        let index = bands.findIndex((band) => Math.abs(node.y - band.top) <= ROW_TOLERANCE);
        if (index === -1) {
            bands.push({ top: node.y, bottom: node.y + height, avgHeight: height });
            index = bands.length - 1;
        }
        const band = bands[index];
        band.bottom = Math.max(band.bottom, node.y + height);
        band.avgHeight = band.bottom - band.top;
        nodeToRow.set(node.id, index);
    });
    const counts = new Map();
    connections.forEach((c) => {
        const a = nodeToRow.get(c.from);
        const b = nodeToRow.get(c.to);
        if (a !== undefined && b !== undefined && a !== b) {
            const key = `${Math.min(a, b)}-${Math.max(a, b)}`;
            counts.set(key, (counts.get(key) || 0) + 1);
        }
    });
    const rowY = new Map();
    let currentY = START_Y;
    bands.forEach((band, i) => {
        rowY.set(i, currentY);
        currentY += i < bands.length - 1 ? band.avgHeight + BASE_GAP + LANE_THICKNESS * (counts.get(`${i}-${i + 1}`) || 0) : band.avgHeight;
    });
    const adjusted = nodes.map((node) => {
        const r = nodeToRow.get(node.id);
        const y = r !== undefined ? rowY.get(r) : undefined;
        return y !== undefined ? { ...node, y } : node;
    });
    return { nodes: adjusted, height: currentY + 200 };
}
function computeConnections(nodes, connections, dims) {
    const byId = new Map(nodes.map((n) => [n.id, n]));
    const point = (node, side, offset = 0) => {
        const d = dims.get(node.id);
        if (!d)
            return { x: node.x, y: node.y };
        const cx = node.x + d.width / 2;
        const cy = node.y + d.height / 2;
        if (side === 'top')
            return { x: cx + offset, y: node.y };
        if (side === 'bottom')
            return { x: cx + offset, y: node.y + d.height };
        if (side === 'left')
            return { x: node.x, y: cy + offset };
        if (side === 'right')
            return { x: node.x + d.width, y: cy + offset };
        return { x: cx, y: cy };
    };
    const rowInfo = new Map();
    nodes.forEach((node) => {
        const d = dims.get(node.id);
        const info = rowInfo.get(node.row) || { top: Infinity, bottom: -Infinity };
        info.top = Math.min(info.top, node.y);
        info.bottom = Math.max(info.bottom, node.y + (d ? d.height : 0));
        rowInfo.set(node.row, info);
    });
    const meta = new Map();
    const exits = new Map();
    const entries = new Map();
    connections.forEach((c) => {
        const from = byId.get(c.from);
        const to = byId.get(c.to);
        if (!from || !to)
            return;
        let m;
        if (from.id === to.id)
            m = { dir: 'self', fromSide: 'right', toSide: 'right' };
        else if (to.row > from.row)
            m = { dir: 'down', fromSide: from.col === 3 ? 'right' : 'bottom', toSide: 'top' };
        else if (to.row < from.row)
            m = { dir: 'up', fromSide: 'top', toSide: 'bottom' };
        else
            m = to.col > from.col ? { dir: 'right', fromSide: 'right', toSide: 'left' } : { dir: 'left', fromSide: 'left', toSide: 'right' };
        meta.set(c.id, m);
        if (m.dir !== 'self') {
            const ek = `${from.id}-${m.fromSide}`;
            const nk = `${to.id}-${m.toSide}`;
            exits.set(ek, [...(exits.get(ek) || []), c.id]);
            entries.set(nk, [...(entries.get(nk) || []), c.id]);
        }
    });
    const LANE_OFFSET = 18;
    const spread = (groups, step) => {
        const out = new Map();
        groups.forEach((ids) => ids.forEach((id, idx) => out.set(id, (idx - (ids.length - 1) / 2) * step)));
        return out;
    };
    const exitOffsets = spread(exits, LANE_OFFSET);
    const entryOffsets = spread(entries, LANE_OFFSET);
    const vertical = new Map();
    connections.forEach((c) => {
        const m = meta.get(c.id);
        const from = byId.get(c.from);
        const to = byId.get(c.to);
        if (!m || !from || !to || (m.dir !== 'down' && m.dir !== 'up'))
            return;
        const key = `${Math.min(from.row, to.row)}-${Math.max(from.row, to.row)}`;
        vertical.set(key, [...(vertical.get(key) || []), c.id]);
    });
    const verticalY = spread(vertical, 18);
    const out = [];
    connections.forEach((c) => {
        const from = byId.get(c.from);
        const to = byId.get(c.to);
        const m = meta.get(c.id);
        if (!from || !to || !m)
            return;
        let d;
        let start;
        if (m.dir === 'self') {
            const fd = dims.get(from.id);
            if (!fd)
                return;
            const sx = from.x + fd.width;
            const sy = from.y + fd.height / 2;
            start = { x: sx, y: sy };
            d = buildSmoothPath([start, { x: sx + 60, y: sy }, { x: sx + 60, y: sy - 60 }, { x: sx, y: sy - 60 }, { x: sx, y: sy }]);
        }
        else {
            start = point(from, m.fromSide, exitOffsets.get(c.id) || 0);
            const end = point(to, m.toSide, entryOffsets.get(c.id) || 0);
            const points = [start];
            if (m.dir === 'down' || m.dir === 'up') {
                const upper = rowInfo.get(Math.min(from.row, to.row));
                const lower = rowInfo.get(Math.max(from.row, to.row));
                const corridorY = (upper && lower ? (upper.bottom + lower.top) / 2 : (start.y + end.y) / 2) + (verticalY.get(c.id) || 0);
                const laneOffsetX = exitOffsets.get(c.id) || 0;
                if (m.fromSide === 'right' || m.fromSide === 'left') {
                    const laneX = start.x + (m.fromSide === 'right' ? 40 : -40) + laneOffsetX;
                    points.push({ x: laneX, y: start.y }, { x: laneX, y: corridorY }, { x: end.x, y: corridorY });
                }
                else {
                    const laneX = start.x + laneOffsetX;
                    const stubY = m.fromSide === 'bottom' ? start.y + 40 : start.y - 40;
                    points.push({ x: start.x, y: stubY }, { x: laneX, y: stubY }, { x: laneX, y: corridorY }, { x: end.x, y: corridorY });
                }
                const STUB = 24;
                const approach = m.toSide === 'top' ? { x: end.x, y: end.y - STUB } : m.toSide === 'bottom' ? { x: end.x, y: end.y + STUB } : m.toSide === 'left' ? { x: end.x - STUB, y: end.y } : { x: end.x + STUB, y: end.y };
                points.push(approach, end);
            }
            else {
                points.push(end);
            }
            d = buildSmoothPath(points);
        }
        out.push({ id: c.id, d, start, color: CONNECTION_COLORS[c.color] });
    });
    return out;
}
function NodeCard({ node, stopped, info, innerRef }) {
    return (_jsxs("div", { ref: innerRef, className: "absolute z-[1] w-[320px] rounded-lg border bg-card text-card-foreground shadow-sm", style: { left: node.x, top: node.y }, children: [_jsxs("div", { className: "border-b p-4 pb-3", children: [_jsxs("div", { className: "flex min-w-0 items-center gap-2", children: [_jsx("span", { className: "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary", children: _jsx(Database, { className: "h-4 w-4" }) }), _jsx("span", { className: "min-w-0 flex-1 truncate text-sm font-semibold", title: node.title, children: node.title }), (info === null || info === void 0 ? void 0 : info.tag) ? (_jsx("span", { className: info.tag === 'most'
                                    ? 'shrink-0 rounded-full bg-orange-50 px-2 py-0.5 text-[11px] font-medium text-orange-800 dark:bg-orange-500/10 dark:text-orange-300'
                                    : 'shrink-0 rounded-full bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-700 dark:bg-green-500/10 dark:text-green-400', children: info.tag === 'most' ? 'mais paradas' : 'menor parada' })) : null] }), node.stepNumber ? _jsxs("p", { className: "mt-1 text-xs text-muted-foreground", children: ["Etapa ", node.stepNumber] }) : null, node.fields.length ? (_jsx("div", { className: "mt-3 flex flex-wrap gap-1.5", children: node.fields.slice(0, 4).map((field, idx) => (_jsx("span", { className: "flex h-7 w-7 items-center justify-center rounded-md", style: FIELD_TONES[idx % 4].chip, title: field.type, children: _jsx(FieldIcon, { type: field.type, className: "h-3.5 w-3.5" }) }, field.id))) })) : null] }), _jsxs("div", { className: "grid gap-2 p-4 text-[13px]", children: [_jsx(Metric, { label: "Tempo m\u00E9dio", value: `${Math.round(node.avgTimeSeconds || 0)}s` }), _jsx(Metric, { label: "Campos", value: node.fieldsCount }), node.fields.length ? (_jsx("div", { className: "grid gap-1.5", children: node.fields.slice(0, 3).map((field, idx) => (_jsxs("div", { className: "flex items-center gap-2 rounded-md border border-l-[3px] bg-muted/40 px-2.5 py-1.5", style: { borderLeftColor: FIELD_TONES[idx % 4].border }, children: [_jsx(FieldIcon, { type: field.type, className: "h-3.5 w-3.5 shrink-0 text-muted-foreground" }), _jsxs("div", { className: "min-w-0", children: [_jsx("p", { className: "text-[11px] font-medium text-muted-foreground", children: field.type }), _jsx("p", { className: "truncate font-mono text-xs", title: field.id, children: field.displayName })] })] }, field.id))) })) : null, _jsx(Metric, { label: "Entradas", value: node.inputsCount }), _jsx(Metric, { label: "Sa\u00EDdas", value: node.outputsCount }), stopped != null ? (_jsxs("div", { className: "flex items-center justify-between rounded-md bg-orange-50 px-2.5 py-1.5 text-orange-800 dark:bg-orange-500/10 dark:text-orange-300", children: [_jsx("span", { children: "Pararam aqui" }), _jsxs("span", { className: "font-semibold tabular-nums", children: [stopped.toLocaleString('pt-BR'), info && info.arrived > 0 ? ` · ${((stopped / info.arrived) * 100).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%` : ''] })] })) : null] })] }));
}
function Metric({ label, value }) {
    return (_jsxs("div", { className: "flex items-center justify-between rounded-md bg-muted/60 px-2.5 py-1.5", children: [_jsx("span", { className: "text-muted-foreground", children: label }), _jsx("span", { className: "font-semibold tabular-nums", children: value })] }));
}
/** stopped: leads que pararam em cada etapa (do bloco de estatísticas); sem ele, a linha não aparece. */
export function QuizBoard({ board, pageView, stopped, info }) {
    const base = React.useMemo(() => convert(board, pageView), [board, pageView]);
    const refs = React.useRef(new Map());
    const [state, setState] = React.useState(null);
    const [hovered, setHovered] = React.useState(null);
    const scroller = React.useRef(null);
    const drag = React.useRef(null);
    const hasStopped = !!stopped; // a linha "Pararam aqui" muda a altura dos cartões: refaz o traçado
    React.useLayoutEffect(() => {
        const dims = new Map();
        refs.current.forEach((el, id) => dims.set(id, { width: el.offsetWidth, height: el.offsetHeight }));
        const laid = layout(base.nodes, base.connections, dims);
        setState({ nodes: laid.nodes, height: laid.height, drawn: computeConnections(laid.nodes, base.connections, dims) });
        if (scroller.current) {
            scroller.current.scrollLeft = 0;
            scroller.current.scrollTop = 0;
        }
    }, [base, hasStopped]);
    const nodes = (state === null || state === void 0 ? void 0 : state.nodes) || base.nodes;
    const drawn = (state === null || state === void 0 ? void 0 : state.drawn) || [];
    const ordered = hovered ? [...drawn.filter((d) => d.id !== hovered), ...drawn.filter((d) => d.id === hovered)] : drawn;
    // Arrastar para mover o quadro (mouse e toque), como na tela antiga.
    const onPointerDown = (e) => {
        if (!scroller.current)
            return;
        drag.current = { x: e.clientX, y: e.clientY, left: scroller.current.scrollLeft, top: scroller.current.scrollTop };
    };
    const onPointerMove = (e) => {
        if (!drag.current || !scroller.current)
            return;
        scroller.current.scrollLeft = drag.current.left - (e.clientX - drag.current.x);
        scroller.current.scrollTop = drag.current.top - (e.clientY - drag.current.y);
    };
    const stop = () => {
        drag.current = null;
    };
    return (_jsx("div", { ref: scroller, onPointerDown: onPointerDown, onPointerMove: onPointerMove, onPointerUp: stop, onPointerLeave: stop, className: "relative h-[calc(100vh-320px)] min-h-[440px] cursor-grab touch-pan-x touch-pan-y select-none overflow-auto rounded-xl border bg-muted/30 active:cursor-grabbing", style: { backgroundImage: 'radial-gradient(circle, hsl(var(--border)) 1px, transparent 1px)', backgroundSize: '20px 20px' }, "aria-label": "Board do quiz: arraste para mover", children: _jsxs("div", { className: "relative mx-auto my-6", style: { width: SVG_WIDTH, height: (state === null || state === void 0 ? void 0 : state.height) || 800 }, children: [_jsx("svg", { className: "absolute left-0 top-0 z-0", width: SVG_WIDTH, height: (state === null || state === void 0 ? void 0 : state.height) || 800, "aria-hidden": "true", children: ordered.map((c) => {
                        const active = hovered === c.id;
                        const arrowId = `arrow-${c.id.replace(/[^a-zA-Z0-9_-]/g, '')}`;
                        return (_jsxs("g", { onMouseEnter: () => setHovered(c.id), onMouseLeave: () => setHovered(null), className: "cursor-pointer", children: [_jsx("defs", { children: _jsx("marker", { id: arrowId, markerWidth: 9, markerHeight: 9, refX: 8, refY: 4.5, orient: "auto", markerUnits: "userSpaceOnUse", children: _jsx("path", { d: "M 0 0 L 9 4.5 L 0 9 z", fill: c.color }) }) }), _jsx("path", { d: c.d, stroke: "hsl(var(--background) / 0.6)", strokeWidth: active ? 14 : 10, fill: "none", strokeLinecap: "round", strokeLinejoin: "round" }), _jsx("path", { d: c.d, stroke: c.color, strokeWidth: active ? 4 : 2.5, fill: "none", strokeLinecap: "round", strokeLinejoin: "round", markerEnd: `url(#${arrowId})` }), _jsx("circle", { cx: c.start.x, cy: c.start.y, r: active ? 5 : 4, fill: c.color, stroke: "hsl(var(--background))", strokeWidth: active ? 2.5 : 2 })] }, c.id));
                    }) }), nodes.map((node) => (_jsx(NodeCard, { node: node, stopped: stopped ? stopped[node.id] || 0 : null, info: info === null || info === void 0 ? void 0 : info[node.id], innerRef: (el) => {
                        if (el)
                            refs.current.set(node.id, el);
                        else
                            refs.current.delete(node.id);
                    } }, node.id)))] }) }));
}
